import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireRole?: 'admin' | 'superadmin';
}

export function ProtectedRoute({ children, requireRole }: ProtectedRouteProps) {
    const { isAuthenticated, isLoading, user } = useAuthStore();
    const location = useLocation();

    if (isLoading) {
        return (
            <div className="h-screen w-full flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!isAuthenticated) {
        // Redirect to the login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience.
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (user && !user.onboardingCompleted && location.pathname !== '/onboarding') {
        return <Navigate to="/onboarding" replace />;
    }

    if (requireRole) {
        const role = user?.role;
        const isAllowed =
            role === 'superadmin' ||
            (requireRole === 'admin' && role === 'admin');
        if (!isAllowed) {
            return <Navigate to="/dashboard" replace />;
        }
    }

    return <>{children}</>;
}
